import { ComponentType, FC } from "react";
import { type NavLinkProps } from "react-router-dom";

import { Link } from "@/components/_molecules/navigation-controls/link";
import { NavLink } from "@/components/_molecules/navigation-controls/nav-link";
import { cn } from "@/lib/cn";

interface HeaderNavElementProps extends Omit<NavLinkProps, "children"> {
  isSelected?: boolean;
  icon?: ComponentType<{ className?: string } & React.AriaAttributes>;
  variant?: "pill" | "grouped";
  children: React.ReactNode;
}

export const HeaderNavElement: FC<HeaderNavElementProps> = ({
  isSelected,
  icon: Icon,
  variant = "grouped",
  children,
  ...props
}: HeaderNavElementProps) => {
  if (isSelected !== undefined) {
    return (
      <Link
        {...props}
        style={
          typeof props.style === "function"
            ? props.style({ isActive: isSelected, isPending: false, isTransitioning: false })
            : props.style
        }
        aria-current={isSelected ? "page" : undefined}
        className={cn(
          "my-0 flex h-7 select-none items-center gap-1.5 rounded-md px-2.5 text-xs",
          variant === "grouped" && "border border-border",
          isSelected
            ? "bg-secondary font-medium text-secondary-foreground"
            : "bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        )}
      >
        {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : null}
        {children}
      </Link>
    );
  }
  if (variant === "grouped") {
    return (
      <NavLink
        {...props}
        className="flex h-7 select-none items-stretch overflow-hidden rounded-md border border-border bg-background text-xs"
      >
        {({ isActive }) => (
          <span
            className={cn(
              "flex w-full items-center gap-1.5 overflow-hidden text-ellipsis px-2.5",
              isActive
                ? "bg-secondary font-medium text-secondary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
          >
            {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : null}
            {children}
          </span>
        )}
      </NavLink>
    );
  }

  return (
    <div className="relative rounded-md">
      <NavLink {...props} className="flex select-none flex-row items-center text-sm">
        {({ isActive }) => (
          <span
            className={cn(
              "relative flex h-7 items-center gap-1.5 overflow-hidden text-ellipsis rounded-md px-2.5 text-xs hover:bg-foreground/3 hover:text-accent-foreground",
              isActive ? "bg-foreground/6 font-medium text-accent-foreground" : "text-muted-foreground",
            )}
          >
            {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : null}
            {children}
          </span>
        )}
      </NavLink>
    </div>
  );
};
